/* eslint-disable react/jsx-no-bind */
import React from 'react';
import Table from 'reactstrap/lib/Table';
import { IBackOrderListEntity } from '../../../actions/DataServiceEntities.g';
import { ListGroup, ListGroupItem, ListGroupItemHeading, ListGroupItemText } from 'reactstrap';
import { ICoreContext } from '@msdyn365-commerce/core';
import { format } from '@msdyn365-commerce-modules/utilities';

export interface IBackOrderTableComponentResources {
    backOrderListTitleText: string;
    backOrderListItemsText: string;
    orderNumberHeadingText: string;
    poNumberHeadingText: string;
    mfrHeadingText: string;
    partNumberHeadingText: string;
    deliveryTypeHeadingText: string;
    qtyHeadingText: string;
    totalHeadingText: string;
    noBackOrdersMessageText: string;
}

interface IBackOrderTableComponentProps {
    backOrders: IBackOrderListEntity[];
    resources: IBackOrderTableComponentResources;
    totalCount: number;
    ctx: ICoreContext;
    sortingDropdown: JSX.Element;
    sortOrderButton: JSX.Element;
    currencyFormatter: (price: string | number, currencyCode?: string | undefined) => string;
}

export const BackOrderTableComponent: React.FC<IBackOrderTableComponentProps> = props => {
    const { backOrders, resources, totalCount, sortOrderButton, sortingDropdown, currencyFormatter } = props;
    const {
        backOrderListItemsText,
        orderNumberHeadingText,
        poNumberHeadingText,
        mfrHeadingText,
        partNumberHeadingText,
        deliveryTypeHeadingText,
        qtyHeadingText,
        totalHeadingText,
        noBackOrdersMessageText
    } = resources;

    const [outerWidth, setOuterWidth] = React.useState(0);

    React.useEffect(() => {
        //Set initial outerWidth
        setOuterWidth(window.outerWidth);
        //Resize window event handling
        window.removeEventListener('resize', () => setOuterWidth(window.outerWidth));
        window.addEventListener('resize', () => setOuterWidth(window.outerWidth), { passive: true });
        return window.removeEventListener('resize', () => setOuterWidth(window.outerWidth));
    });

    if (outerWidth === 0) {
        return null;
    }

    return (
        <div className={`back-order-results`}>
            {outerWidth > 768 ? (
                <>
                    <div className={'back-order-results__info'}>
                        {totalCount > 0 && (
                            <div className='back-order-results__info-sort'>
                                {sortingDropdown}
                                {sortOrderButton}
                            </div>
                        )}
                        {format(backOrderListItemsText, totalCount)}
                    </div>
                    <Table className={'back-order-results__table'}>
                        <thead>
                            <tr>
                                <th>{orderNumberHeadingText}</th>
                                <th>{poNumberHeadingText}</th>
                                <th>{deliveryTypeHeadingText}</th>
                                <th>{mfrHeadingText}</th>
                                <th>{partNumberHeadingText}</th>
                                <th>{qtyHeadingText}</th>
                                <th>{totalHeadingText}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {backOrders.length > 0 ? (
                                backOrders.map((order, index) => {
                                    const deliveryType = order.DeliveryType === 'None' ? 'Standard' : order.DeliveryType;
                                    return (
                                        <tr key={`backorder-${index}`}>
                                            <td>{order.SalesId}</td>
                                            <td>{order.PurchaseOrderNumber}</td>
                                            <td>{deliveryType}</td>
                                            <td>{order.ManufacturerNumber}</td>
                                            <td>{order.ItemId}</td>
                                            <td>{order.Quantity}</td>
                                            <td>{currencyFormatter(order.Total || 0)}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr className='back-order-results__table--no-backorders'>
                                    <td>{noBackOrdersMessageText}</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </>
            ) : (
                <ListGroup className={'back-order-results__list'}>
                    <div>
                        <h5>{format(backOrderListItemsText, totalCount)}</h5>
                        {backOrders.map((order, index) => {
                            const deliveryType = order.DeliveryType === 'None' ? 'Standard' : order.DeliveryType;
                            return (
                                <ListGroupItem key={`backorder-${index}`}>
                                    <ListGroupItemHeading>
                                        <div className={'back-order-results__list-double-data'}>
                                            <span className={'back-order-results__list-double-data--id'}>{order.ItemId}</span>
                                            <span className={'back-order-results__list-double-data--po'}>{deliveryType}</span>
                                        </div>
                                    </ListGroupItemHeading>
                                    <ListGroupItemText>
                                        <b>{`${mfrHeadingText} : `}</b>
                                        {order.ManufacturerNumber}
                                    </ListGroupItemText>
                                    <ListGroupItemText>
                                        <div className={'back-order-results__list-double-data'}>
                                            <span className={'back-order-results__list-double-data--id'}>{order.SalesId}</span>
                                            {order.PurchaseOrderNumber && (
                                                <span className={'back-order-results__list-double-data--po'}>
                                                    <b>{`${poNumberHeadingText} `}</b>
                                                    {order.PurchaseOrderNumber}
                                                </span>
                                            )}
                                        </div>
                                    </ListGroupItemText>
                                    <ListGroupItemText>
                                        <div className={'back-order-results__list-double-data'}>
                                            <span className={'back-order-results__list-double-data--qty'}>
                                                <b>{`${qtyHeadingText} : `}</b>
                                                {order.Quantity}
                                            </span>
                                            <span className={'back-order-results__list-double-data--price'}>
                                                <b>{`${totalHeadingText} : `}</b>
                                                {currencyFormatter(order.Total || 0)}
                                            </span>
                                        </div>
                                    </ListGroupItemText>
                                </ListGroupItem>
                            );
                        })}
                    </div>
                </ListGroup>
            )}
        </div>
    );
};
